import { useKeycloak } from "@react-keycloak/web";
import { APP_ID, TG_DATA } from "../_app/consts";
import { Platform } from "../_app/types";
import { HubError } from "../class/Error";
import { _formatDate } from "../utils/_formatDate";
import { useWebApp } from "@vkruglikov/react-telegram-web-app";
import { getProfile, loginWithPasswordGrant } from "../utils/async";
import { decodeUserHash } from "../utils/utils";
import { useAppNavigate } from "./use-app-navigate";
import { useDispatch } from "react-redux";
import isTokenValid from "../utils/isTokenValid";
import Keycloak from "keycloak-js";
import { jwtDecode } from "jwt-decode";

export interface FetchOptions {
  platform?: boolean;
  tg?: boolean;
  locale?: string;
  xPublicKey?: string;
}

const reFetch = async <T>(
  uri: string,
  method: RequestInit["method"],
  json?: any,
  options?: FetchOptions
): Promise<T> => {
  const token = localStorage.getItem("token" + APP_ID);

  const authHeaders = token && {
    Authorization: `Bearer ${token}`,
  };
  const platformHeaders = options &&
    options.platform && {
      Platform: Platform.web_mobile,
    };
  const tgPlatform = options &&
    options.tg && {
      Platform: Platform.telegram_mini_app,
    };
  const locale = options &&
    options.locale && {
      Locale: options.locale,
    };

  const response = await fetch(`${uri}`, {
    method: method,
    body: json ? JSON.stringify(json) : undefined,
    headers: {
      "Content-Type": "application/json",
      ...tgPlatform,
      ...platformHeaders,
      ...authHeaders,
    },
  });

  if (!response.ok) {
    let errorDetails;
    try {
      errorDetails = await response.json();
    } catch (e) {}

    if (response.status === 400 || response.status === 403) {
      const errorMessage = errorDetails.message || "Unknown error";
      const detailedMessage = errorDetails.detailedMessage || "";
      throw new HubError(
        errorDetails.errorCode,
        errorMessage,
        detailedMessage,
        errorDetails.errorCode
      );
    }

    if (response.status === 500) {
      const timestamp = errorDetails?.timestamp;
      throw new HubError("500", "500 Internal Server Error " + _formatDate(timestamp), "", "");
    }
    await HubError.throwAsync(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (response.status === 202) {
    return undefined as T;
  }

  if (response.status === 200) {
    return await response.json();
  }

  return undefined as T;
};

const login = async (token: string, initData: string, keycloak: Keycloak) => {
  try {
    const response: any = await loginWithPasswordGrant(
      decodeURIComponent(initData),
      decodeUserHash(initData),
      keycloak
    );
    if (response.access_token) {
      console.log("success update token");
      return response.access_token;
    }
  } catch (error: any) {
    console.error("Login failed:", error);
    return token;
  }
};

export const useFetch = () => {
  const { keycloak } = useKeycloak();
  const WebApp = useWebApp();
  const navigate = useAppNavigate();
  const dispatch = useDispatch();

  const _fetch = async <T>(
    uri: string,
    method: RequestInit["method"],
    json?: any,
    options?: FetchOptions
  ): Promise<T> => {
    let token = localStorage.getItem("token" + APP_ID);
    // let token = 'eyJhbGciOiJSUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICJyTTEwOE02MlF4Zk1VNFVjS2tReS1sdkFEeVdDTkU4MnI3LUdvcVR2a0NrIn0.eyJleHAiOjE3Mzc2OTkyNDksImlhdCI6MTczNzY5ODk0OSwianRpIjoiY2ZhNWMwMGItMDkzZC00YjA1LTkyMDAtMThlMDk3NjNkNTY3IiwiaXNzIjoiaHR0cHM6Ly9hZHJvbnRlc3QuY29tOjk0NDMvcmVhbG1zL2Fkcm9uLXRlc3QiLCJhdWQiOiJhY2NvdW50Iiwic3ViIjoiOGU4NWUxOGItOTNkMS00NWJmLTkwYmMtNzlmODBiY2ZmODU0IiwidHlwIjoiQmVhcmVyIiwiYXpwIjoid2ViYXBwIiwic2lkIjoiYzkzNmM5MWQtODk0Zi00Yjk3LTg3YTQtYmViZjM1YjVkMzA2IiwiYWNyIjoiMSIsImFsbG93ZWQtb3JpZ2lucyI6WyJodHRwczovL2Fkcm9udGVzdC5jb20iLCJodHRwOi8vMTkyLjE2OC4xLjI6MzAwMCIsImh0dHA6Ly9sb2NhbGhvc3Q6MzAwMCJdLCJyZWFsbV9hY2Nlc3MiOnsicm9sZXMiOlsib2ZmbGluZV9hY2Nlc3MiLCJ1bWFfYXV0aG9yaXphdGlvbiIsImRlZmF1bHQtcm9sZXMtYWRyb24tdGVzdCJdfSwicmVzb3VyY2VfYWNjZXNzIjp7ImFjY291bnQiOnsicm9sZXMiOlsibWFuYWdlLWFjY291bnQiLCJtYW5hZ2UtYWNjb3VudC1saW5rcyIsInZpZXctcHJvZmlsZSJdfX0sInNjb3BlIjoicHJvZmlsZSBlbWFpbCIsImVtYWlsX3ZlcmlmaWVkIjpmYWxzZSwicHJvZmlsZUlkIjoiNzBiYjg3ZTktZDIxZS00MWVhLTlkMjQtOWU3NWI0ODA5ODExIiwibmFtZSI6ItCU0LXQvdC40YEg0J_RgNC-0LrQvtC_0LXQvdC60L7QsiIsInByZWZlcnJlZF91c2VybmFtZSI6ImRlbmlzX3Byb2tvcGVua29mZiIsImdpdmVuX25hbWUiOiLQlNC10L3QuNGBIiwiZmFtaWx5X25hbWUiOiLQn9GA0L7QutC-0L_QtdC90LrQvtCyIn0.b7JTZYwdjvsTSNaZd-N5aOEMhkpQOj0aHGeSY8Vy6OmAk4LL02NxaCcmIrlgloheoveco5sE7AscWsOxxXamluUB9jup1yXXbNu_cPrRVKYygTbWxJeSh3O5i3PStiyAa0L3PBpZZfFZCDVcqho_vbwisEQ5Ek7z6GkHYSmbUu8bJK3wrrrOpBFW9YkrqXXIbpYfAb6SSY-FjyIvluxE6kqsxlgT68hHDghzRhqJ5vD_G4_af18cvkNdxrdVWMAXeIl_5_v2pMRUxrVLbHP7anSBcPpJDA07uSnODU5DDDaNSY58E0q-x1tvJZpJ28aPCIXM1Lg_0FAK9MD_rmbzMA';

    if (token && !isTokenValid(token) && WebApp) {
      console.log("token is not valid");
      const initData = process.env.NODE_ENV === "development" ? TG_DATA : WebApp.initData;
      token = await login(token, initData, keycloak);
    }

    // console.log(jwtDecode(token).exp);

    const authHeaders = token && {
      Authorization: `Bearer ${token}`,
    };

    const platformHeaders = options &&
      options.platform && {
        Platform: Platform.web_mobile,
      };
    const tgPlatform = options &&
      options.tg && {
        Platform: Platform.telegram_mini_app,
      };
    const locale = options &&
      options.locale && {
        Locale: options.locale,
      };

    const xPublicKey = options &&
      options.xPublicKey && {
        "X-Public-Key": options.xPublicKey,
      };

    const response = await fetch(`${uri}`, {
      method: method,
      body: json ? JSON.stringify(json) : undefined,
      headers: {
        "Content-Type": "application/json",
        ...tgPlatform,
        ...platformHeaders,
        ...authHeaders,
        ...xPublicKey,
      },
    });

    if (!response.ok) {
      console.log("!response.ok", response.status, response.statusText);
      let errorDetails;
      try {
        errorDetails = await response.json();
      } catch (e) {}

      if (response.status === 400 || response.status === 403) {
        const errorMessage = errorDetails.message || "Unknown error";
        const detailedMessage = errorDetails.detailedMessage || "";
        throw new HubError(
          errorDetails.errorCode,
          errorMessage,
          detailedMessage,
          errorDetails.errorCode
        );
      }

      if (response.status === 401) {
        if (WebApp) {
          // const initData = process.env.NODE_ENV === 'development' ? TG_DATA: WebApp.initData;
          // const login = async () => {
          //     try {
          //         const response: any = await loginWithPasswordGrant(decodeURIComponent(initData), decodeUserHash(initData), keycloak);
          //         if(response.access_token) {
          //             console.log('re-fetch trying');
          //             return await reFetch(uri, method, json, options);
          //         }
          //         // getProfile(response.access_token, dispatch, navigate);
          //         // return reFetch(uri, method, json, options);
          //     } catch (error: any) {
          //         console.error("Login failed:", error);
          //     }
          // };
          // login();
        }
      }

      if (response.status === 500) {
        const timestamp = errorDetails?.timestamp;
        throw new HubError("500", "500 Internal Server Error " + _formatDate(timestamp), "", "");
      }
      await HubError.throwAsync(response);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (response.status === 202) {
      return undefined as T;
    }

    if (response.status === 200) {
      return await response.json();
    }

    return undefined as T;
  };

  return _fetch;
};
